
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import type { Notification } from '@/lib/notifications/types';

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isClearingAll, setIsClearingAll] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) {
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      
      // Cast the data to match our Notification type
      const typedNotifications: Notification[] = (data || [])
        .map(item => ({
          ...item,
          type: item.type as Notification['type'],
          data: (item.data as any) || {},
          action_url: item.action_url || undefined,
          action_label: item.action_label || undefined,
          expires_at: item.expires_at || undefined,
          is_read: item.is_read || false
        }));
      
      setNotifications(typedNotifications);
    } catch (err: any) {
      console.error('❌ Error fetching notifications:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true }
            : notification
        )
      );
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!user || isClearingAll) {
      return;
    }

    try {
      setIsClearingAll(true);
      
      // Optimistically update the UI first for immediate feedback
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );

      // Then update the database
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        // Revert the optimistic update by refetching
        fetchNotifications();
        throw error;
      }

      // Wait a moment before allowing polling to resume
      setTimeout(() => {
        setIsClearingAll(false);
      }, 2000);
      
    } catch (err: any) {
      console.error('Error marking all notifications as read:', err);
      setIsClearingAll(false);
    }
  }, [user, fetchNotifications, isClearingAll]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      // Optimistically remove from UI first
      setNotifications(prev => 
        prev.filter(notification => notification.id !== notificationId)
      );

      // Perform the actual deletion
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) {
        console.error('Error deleting notification:', error);
        // Revert the optimistic update by refetching
        fetchNotifications();
        throw error;
      }
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      // The fetchNotifications call above will restore the notification if deletion failed
    }
  }, [fetchNotifications]);

  useEffect(() => {
    if (!user) return;

    // Initialize notifications with polling
    
    // Initial fetch
    fetchNotifications();

    // Safety mechanism: reset isClearingAll if it gets stuck
    const safetyTimeout = setTimeout(() => {
      if (isClearingAll) {
        setIsClearingAll(false);
      }
    }, 10000); // 10 seconds safety timeout

    // Set up polling for notifications every 30 seconds
    const pollNotifications = () => {
      if (document.visibilityState === 'visible' && !isClearingAll) {
        fetchNotifications();
      }
    };

    const intervalId = setInterval(pollNotifications, 30000);

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isClearingAll) {
        fetchNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      clearTimeout(safetyTimeout);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, fetchNotifications, isClearingAll]);

  // Calculate unread count from current notifications state
  const unreadCount = notifications.filter(n => !n.is_read).length;

  const forceRefresh = useCallback(() => {
    setIsClearingAll(false); // Reset clearing state
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refetch: fetchNotifications,
    forceRefresh
  };
}
